import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { wishlistService } from '../../services/api';
import { getImageUrl, getProductImageUrl } from '../../utils/imageHelper';
import StarRating from '../StarRating/StarRating';
import styles from './ProductCard.module.css';

const ProductCard = ({ product, onAddToCart, isInCart, quantityInCart }) => {
  const [added, setAdded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkingFavorite, setCheckingFavorite] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addNotification } = useNotifications();

  // Vérifier si le produit est dans les favoris
  useEffect(() => {
    if (isAuthenticated && product?.id) {
      checkFavoriteStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, product?.id]);

  const checkFavoriteStatus = async () => {
    try {
      setCheckingFavorite(true);
      const response = await wishlistService.checkWishlist(product.id);
      if (response.success) {
        setIsFavorite(response.data?.is_favorite || false);
      }
    } catch (error) {
      // Ignorer l'erreur silencieusement
      console.warn('Erreur lors de la vérification des favoris:', error);
    } finally {
      setCheckingFavorite(false);
    }
  };



  const handleToggleFavorite = async (e) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        const response = await wishlistService.removeFromWishlist(product.id);
        if (response.success) {
          setIsFavorite(false);
          addNotification('Produit retiré des favoris', 'info');
        } else {
          addNotification(response.message || 'Erreur lors de la suppression', 'error');
        }
      } else {
        const response = await wishlistService.addToWishlist(product.id);
        if (response.success) {
          setIsFavorite(true);
          addNotification('Produit ajouté aux favoris', 'success');
        } else {
          addNotification(response.message || 'Erreur lors de l\'ajout', 'error');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la gestion des favoris:', error);
      addNotification('Erreur lors de la gestion des favoris', 'error');
    }
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();

    if (onAddToCart) {
      try {
        const result = await onAddToCart(product.id);
        // Vérifier si result existe et si success est true
        if (result && typeof result === 'object' && result.success === true) {
          setAdded(true);
          addNotification(`${product.name} ajouté au panier`, 'success');
          setTimeout(() => setAdded(false), 1500);
        } else if (result && typeof result === 'object' && result.success === false) {
          // Seulement afficher une erreur si result.success est explicitement false
          addNotification(result.error || 'Erreur lors de l\'ajout au panier', 'error');
        } else {
          // Si result est undefined ou n'a pas la structure attendue, considérer comme succès
          // (pour compatibilité avec les anciennes implémentations)
          setAdded(true);
          addNotification(`${product.name} ajouté au panier`, 'success');
          setTimeout(() => setAdded(false), 1500);
        }
      } catch (error) {
        console.error('Erreur lors de l\'ajout au panier:', error);
        addNotification('Erreur lors de l\'ajout au panier', 'error');
      }
    } else {
      setAdded(true);
      addNotification(`${product.name} ajouté au panier`, 'success');
      setTimeout(() => setAdded(false), 1500);
    }
  };

  const handleCardClick = () => {
    navigate(`/product/${product.id}`);
  };

  // Obtenir l'image du produit en priorisant primary_image puis images[0]
  const productImage = product.primary_image
    ? getProductImageUrl(product.primary_image)
    : (product.images?.[0] ? getProductImageUrl(product.images[0]) : '/images/leaves-right.png');

  // Gérer les prix - convertir en nombre
  const currentPrice = parseFloat(product.sale_price || product.price || 0);
  const oldPrice = product.sale_price ? parseFloat(product.price) : null;

  // Gérer le stock
  const isOutOfStock = product.stock_quantity <= 0;
  const isLowStock = product.stock_quantity <= (product.min_stock_alert || 5);

  return (
    <div className={styles.productCard} onClick={handleCardClick}>
        <div className={styles.productImage}>
          <img src={productImage} alt={product.name} />
          {product.is_featured && (
            <div className={styles.featuredBadge}>En vedette</div>
          )}
          {product.is_organic && (
            <div className={styles.organicBadge}>Bio</div>
          )}
          {isOutOfStock && (
            <div className={styles.outOfStockBadge}>Rupture de stock</div>
          )}
          {isAuthenticated && (
            <button
              className={styles.favoriteButton}
              onClick={handleToggleFavorite}
              title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <svg
                className={styles.heartIcon}
                fill={isFavorite ? "currentColor" : "none"}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </button>
          )}
        </div>

      <div className={styles.productInfo}>
        <h3 className={styles.productTitle}>{product.name}</h3>

        {product.short_description && (
          <p className={styles.productDescription}>{product.short_description}</p>
        )}

        <div className={styles.productDetails}>
          {product.unit && (
            <span className={styles.unit}>Par {product.unit}</span>
          )}
          {product.origin && (
            <span className={styles.origin}>{product.origin}</span>
          )}
        </div>

        <div className={styles.productPrice}>
          <span className={styles.currentPrice}>{currentPrice.toFixed(0)} FCFA</span>
          {oldPrice && <span className={styles.oldPrice}>{oldPrice.toFixed(0)} FCFA</span>}
        </div>

        {product.rating > 0 && (
          <div className={styles.rating}>
            <StarRating rating={product.rating} size="small" showNumber={true} />
            {product.review_count > 0 && (
              <span className={styles.reviewCount}>({product.review_count})</span>
            )}
          </div>
        )}

        <button
          className={`${styles.addToCart} ${isInCart ? styles.inCart : ''} ${isOutOfStock ? styles.disabled : ''}`}
          onClick={handleAddToCart}
          disabled={isOutOfStock}
          style={added ? { backgroundColor: '#4CAF50' } : {}}
        >
          {isOutOfStock ? 'Rupture de stock' :
           isInCart ? `Dans le panier (${quantityInCart})` :
           added ? 'Ajouté !' : 'Ajouter au panier'}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;